import React, {
  useState, useCallback, useMemo, useEffect, useLayoutEffect, useRef,
} from 'react';
import Box from '@mui/material/Box';
import type Uppy from '@uppy/core';

import MediaDropzone from '../../components/media-dropzone';
import { useUploadFileGroups } from '../../components/hooks/use-upload-file-groups';
import ModalMediaViewer from '../media/modal-media-viewer';
import type { DisplayableItem } from '../media/display/media-displayer';
import type { MultiFileEntry } from '../../components/machines/multi-file-uploader-types';
import type { UniversalMediaUploaderEvent, UppyMeta, UppyBody } from '../../components/machines/universal-media-uploader-types';
import { EVENT_TYPES } from '../../components/machines/universal-media-uploader-types';

import type { HydratedMediaItem, MediaType } from './media-type-config';
import type { Image, ModalProps } from './media-props';
import UploadStatusEntry from './media/upload-status-entry';
import TrackUploadDropzone from './media/track-upload-dropzone';
import { useCommittedFileAutoRemoval } from './hooks/use-committed-file-auto-removal';
import { itemContainerSx, itemDividerSx, trackSeparatorSx } from './media/styles';

// ---------------------------------------------------------------------------
// Local styles
// ---------------------------------------------------------------------------

const containerSx = {
  mt: '4px',
  mb: '4px',
  verticalAlign: 'top',
  pt: '10px',
};

// ---------------------------------------------------------------------------
// Shared hook — viewer state, item hydration
// ---------------------------------------------------------------------------

function useMediaViewer(items: Image[], modalProps?: ModalProps) {
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  const hydratedItems: HydratedMediaItem[] = useMemo(() => items.map((item, index) => ({
    ...item,
    onOpenViewer: modalProps
      ? () => setViewerIndex(index)
      : undefined,
  })), [items, modalProps]);

  const handleCloseViewer = useCallback(() => {
    setViewerIndex(null);
  }, []);

  const displayableItems: DisplayableItem[] = useMemo(() => {
    if (!modalProps?.toDisplayableItem) return [];

    return modalProps.mediaItems
      .map((mediaItem) => modalProps.toDisplayableItem!(mediaItem))
      .filter(Boolean);
  }, [modalProps]);

  const initialDisplayableItem = useMemo(() => {
    if (viewerIndex == null || !modalProps?.toDisplayableItem) return undefined;

    const mediaItem = modalProps.mediaItems[viewerIndex];

    if (!mediaItem) return undefined;

    return modalProps.toDisplayableItem(mediaItem);
  }, [viewerIndex, modalProps]);

  return {
    hydratedItems, viewerIndex, handleCloseViewer, displayableItems, initialDisplayableItem,
  };
}

// ---------------------------------------------------------------------------
// MediaField — new universal uploader
// ---------------------------------------------------------------------------

interface MediaFieldProps {
  items: Image[];
  renderItem: (item: HydratedMediaItem) => React.ReactNode;
  modalProps?: ModalProps;
  editing: boolean;
  disabled: boolean;
  uploaderProps: {
    uppy: Uppy<UppyMeta, UppyBody> | null;
    mediaType: MediaType;
    allowedFileTypes: string[];
    shouldShowUploader: boolean;
    files: Map<string, MultiFileEntry>;
    sendEvent: (event: UniversalMediaUploaderEvent) => void;
    wasRecentlyUploaded: boolean;
    suppressFileRemoved: (fileId: string) => void;
    unsuppressFileRemoved: (fileId: string) => void;
  };
  /** Fallback click handler for completed status cards when no viewer is available (e.g. attachments). */
  onCompletedStatusClick?: (fileName: string, fileData: Blob) => void;
  trackEnabled?: boolean;
  uploadTrack?: (mediaID: string, file: File, progress: (...args: unknown[]) => void) => Promise<unknown>;
}

const MediaField: React.FC<MediaFieldProps> = ({
  disabled,
  uploaderProps,
  items,
  modalProps,
  renderItem,
  editing,
  onCompletedStatusClick,
  trackEnabled = false,
  uploadTrack,
}) => {
  const { uploading, errors, completed } = useUploadFileGroups(uploaderProps.files);
  const {
    hydratedItems, viewerIndex, handleCloseViewer, displayableItems, initialDisplayableItem,
  } = useMediaViewer(items, modalProps);

  // Map attachment IDs from completed uploads to the corresponding item index
  // so clicking a completed status card opens the viewer for that media.
  const attachmentIdToItemIndex = useMemo(() => {
    const hydratedItemIndexByMediaId = new Map<string, number>();
    hydratedItems.forEach((item, index) => {
      hydratedItemIndexByMediaId.set(item.mediaID, index);
    });

    const map = new Map<string, number>();
    uploaderProps.files.forEach((entry) => {
      const attachmentId = entry.attachmentData?.id;
      if (!attachmentId) return;

      const itemIndex = hydratedItemIndexByMediaId.get(attachmentId);
      if (itemIndex != null) map.set(attachmentId, itemIndex);
    });
    return map;
  }, [uploaderProps.files, hydratedItems]);

  // Must be computed before completedUploadMediaIds (which intersects with it).
  const hydratedItemsByMediaId = useMemo(
    () => new Map(hydratedItems.map((h) => [h.mediaID, h])),
    [hydratedItems],
  );

  const uploadStatusEntries = useMemo(() => {
    if (!editing) return [];

    return [
      ...uploading.map((e) => ({
        key: `upload-${e.id}`, id: e.id, file: e.file, progress: e.progress,
      })),
      ...errors.map((e) => ({
        key: `upload-${e.id}`, id: e.id, file: e.file, error: e.error,
      })),
      ...completed.map((e) => {
        const fileEntry = uploaderProps.files.get(e.id);
        return {
          key: `upload-${e.id}`,
          id: e.id,
          file: e.file,
          isCompleted: true as const,
          wasRecentlyUploaded: uploaderProps.wasRecentlyUploaded,
          thumbnailUrl: e.thumbnailUrl,
          attachmentId: fileEntry?.attachmentData?.id,
        };
      }),
    ];
  }, [editing, uploading, errors, completed, uploaderProps.wasRecentlyUploaded, uploaderProps.files]);

  // Only suppress the item card when the upload is confirmed in the field value
  // (hydratedItemsByMediaId), ensuring deferred-commit safety: if the value
  // hasn't been written yet, both cards are shown until commit arrives.
  const completedUploadMediaIds = useMemo(() => {
    if (!editing) return new Set<string>();

    const ids = new Set<string>();
    uploaderProps.files.forEach((entry) => {
      if (entry.error) return;
      const id = entry.attachmentData?.id;
      if (id && hydratedItemsByMediaId.has(id)) ids.add(id);
    });
    return ids;
  }, [editing, uploaderProps.files, hydratedItemsByMediaId]);

  const itemsWithoutActiveUploads = useMemo(
    () => items.filter((item) => !completedUploadMediaIds.has(item.mediaID)),
    [items, completedUploadMediaIds],
  );

  useCommittedFileAutoRemoval({
    editing,
    uppy: uploaderProps.uppy,
    files: uploaderProps.files,
    hydratedItemsByMediaId,
    suppressFileRemoved: uploaderProps.suppressFileRemoved,
    unsuppressFileRemoved: uploaderProps.unsuppressFileRemoved,
  });

  const dismissUploaderFile = useCallback((file: MultiFileEntry['file'] | undefined) => {
    if (file) {
      uploaderProps.sendEvent({ type: EVENT_TYPES.FILE_REMOVED, file });
      try {
        if (uploaderProps.uppy?.getFile(file.id)) {
          uploaderProps.uppy.removeFile(file.id);
        }
      } catch (error) { console.error('Failed to remove file from Uppy:', error); }
    }
  }, [uploaderProps.sendEvent, uploaderProps.uppy]);

  // Track which media IDs have been committed (hydrated) so we can detect
  // when an item is removed from the field value and dismiss the orphaned
  // upload entry immediately — preventing the status card from flashing.
  const everHydratedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!editing) {
      everHydratedIdsRef.current.clear();
    }
  }, [editing]);

  // Record all currently hydrated IDs after each committed render so the orphan
  // check below can distinguish "removed after commit" from "not yet committed".
  // useLayoutEffect keeps the render pure while ensuring the ref is updated
  // before the next render begins.
  useLayoutEffect(() => {
    hydratedItemsByMediaId.forEach((_v, id) => everHydratedIdsRef.current.add(id));
  }, [hydratedItemsByMediaId]);

  // Identify completed upload entries whose committed item has been removed
  // from the field value. These are filtered out of the rendered list so
  // the user never sees a flash of the raw status card after clicking remove.
  const orphanedEntries = useMemo(() => {
    if (!editing) return [];

    return uploadStatusEntries.filter((entry) => {
      if (!('isCompleted' in entry) || !entry.isCompleted) return false;
      const { attachmentId } = entry;
      if (!attachmentId) return false;
      return everHydratedIdsRef.current.has(attachmentId)
        && !hydratedItemsByMediaId.has(attachmentId);
    });
  }, [editing, uploadStatusEntries, hydratedItemsByMediaId]);

  const orphanedIds = useMemo(
    () => new Set(orphanedEntries.map((e) => e.id)),
    [orphanedEntries],
  );

  const visibleUploadStatusEntries = useMemo(
    () => uploadStatusEntries.filter((entry) => !orphanedIds.has(entry.id)),
    [uploadStatusEntries, orphanedIds],
  );

  // Dismiss orphaned entries from the machine asynchronously so the files
  // map is cleaned up. The filtering above prevents the visual flash.
  useEffect(() => {
    orphanedEntries.forEach((entry) => {
      dismissUploaderFile(entry.file);
    });
  }, [orphanedEntries, dismissUploaderFile]);

  const getClickHandler = useCallback((
    entry: (typeof uploadStatusEntries)[number],
    itemIndex: number | undefined,
  ): (() => void) | undefined => {
    if (modalProps && itemIndex != null) {
      return hydratedItems[itemIndex]?.onOpenViewer;
    }

    const isCompleted = 'isCompleted' in entry && entry.isCompleted;
    if (onCompletedStatusClick && isCompleted && entry.file?.data instanceof Blob) {
      const blob = entry.file.data;
      return () => onCompletedStatusClick(entry.file.name ?? 'download', blob);
    }

    return undefined;
  }, [modalProps, hydratedItems, onCompletedStatusClick]);

  return (
    <Box sx={containerSx}>
      {uploaderProps.shouldShowUploader && uploaderProps.uppy && (
        <MediaDropzone
          uppy={uploaderProps.uppy}
          mediaType={uploaderProps.mediaType}
          allowedFileTypes={uploaderProps.allowedFileTypes}
          disabled={disabled}
        />
      )}

      {visibleUploadStatusEntries.map((entry) => {
        const attachmentId = 'attachmentId' in entry ? entry.attachmentId : undefined;
        const itemIndex = attachmentId
          ? attachmentIdToItemIndex.get(attachmentId)
          : undefined;

        const isCompletedEntry = 'isCompleted' in entry && entry.isCompleted;
        const committedItem = (isCompletedEntry && itemIndex != null)
          ? hydratedItems[itemIndex]
          : undefined;

        const isCompletedWithTrack = !!(trackEnabled && uploadTrack && attachmentId
          && isCompletedEntry);

        return (
          <UploadStatusEntry
            key={entry.key}
            entry={entry}
            committedItem={committedItem}
            renderItem={renderItem}
            handleClick={getClickHandler(entry, itemIndex)}
            isCompletedWithTrack={isCompletedWithTrack}
            trackEnabled={trackEnabled}
            uploadTrack={uploadTrack}
            onDismiss={() => dismissUploaderFile(entry.file)}
            fileType={uploaderProps.mediaType}
          />
        );
      })}

      {itemsWithoutActiveUploads.map((item) => {
        const hydratedItem = hydratedItemsByMediaId.get(item.mediaID);

        return (
          <Box
            key={`item-${item.mediaID}`}
            sx={{
              display: 'block',
              ...itemDividerSx,
              ...(trackEnabled && editing ? trackSeparatorSx : {}),
            }}
          >
            <Box sx={itemContainerSx}>
              {hydratedItem ? renderItem(hydratedItem) : null}
            </Box>

            {trackEnabled && uploadTrack && editing && (
              <TrackUploadDropzone
                mediaID={item.mediaID}
                uploadTrack={uploadTrack}
              />
            )}
          </Box>
        );
      })}

      {modalProps && viewerIndex != null && initialDisplayableItem && (
        <ModalMediaViewer
          feature={modalProps.feature}
          mapVisible
          mediaValueItems={displayableItems}
          initialItem={initialDisplayableItem}
          metadata={modalProps.metadata}
          visible
          onClose={handleCloseViewer}
        />
      )}
    </Box>
  );
};

export type { MediaFieldProps };
export default MediaField;
